import _ from 'lodash';
import { getConfig } from '../../../util/configUtil';
import * as moment from 'moment/moment';
import { PATIENT_AGE_TYPE } from '../../../util/patientContextUtil';
import { AgyleNamedIcon } from '../../../components/icons/agyle';
import React from 'react';

export const PATIENT_CONTEXT_TABS_ID = {
    PATIENT: 'patient',
    NOTES: 'notes',
    OBSERVATIONS: 'observations',
    ASSESSMENT: 'assessment',
    FORMS: 'forms',
    DNACPR: 'dnacpr',
    SEPSIS: 'sepsis',
    HISTORY: 'history',
    HISTORY_UHD: 'historyUHD',
    UCR: 'ucr',
    LINKS: 'links',
    UCR_FILTERED: 'ucrFiltered',
    ICE: 'ice',
    TIMELINE: 'timeline',
    SCHEDULER: 'scheduler',
    REGISTRY: 'registry',
    EMPTY: 'empty',
    NOTE_DOCUMENT: 'notesDocument',
};

export const SEE_AND_TREAT_STATUSES = {
    new: 'new',
    draft: 'draft',
    completed: 'completed',
    timestamp: 'timestamp',
};

export const SEPSIS_TOOL_TYPE = {
    COMPLETED: 'COMPLETED',
    COMPLETED_NOT_REQUIRED: 'COMPLETED_NOT_REQUIRED',
    IN_COMPLETED: 'IN_COMPLETED',
    NOT_REQUIRED: 'NOT_REQUIRED',
    NEW: 'NEW',
    CANCELED: 'CANCELED',
};

export const ASSESSMENT_BUTTON_COLOURS = {
    mandatoryEmpty: 'error',
    mandatorySet: 'info',
    notMandatoryEmpty: 'secondary',
    notMandatorySet: 'info',
};

export const PATIENT_BANNER_TYPE = {
    UNSET: 'UNSET',
    AWAITING_DNCPR: 'AWAITING_DNCPR',
    ACTIVE_DNCPR: 'ACTIVE_DNCPR',
    DECEASED: 'DECEASED',
};

export const DEFAULT_DESTINATION_STATE = {
    destination: {
        wardId: null,
        bayId: null,
        bayName: null,
        bedId: null,
        bedName: null,
        hospitalId: null,
    },
};

export const CLINICAL_COURSE_BASE = () => {
    return {
        who: null,
        when: null,
        clinicalCourse: '',
        actionsGivenStatus: '',
        actionsGiven: '',
        adviceGiven: '',
    };
};

export const CLINICAL_COURSE_OPTIONS = [
    { name: 'Planned', value: 'planned', id: 'planned' },
    { name: 'Completed', value: 'completed', id: 'completed' },
];

export const CLINICAL_CONCLUSION_INNER_STATE = {
    who: null,
    login: '',
    when: null,
    status: null,
    transfer: null,
    timestamp: null,
    note: '',
};

export const ECDS_VERSION = [
    { id: 'V3', name: 'ECDS v3' },
    { id: 'V4', name: 'ECDS v4' },
];

export const dynamicWidth = 36;

export const isClinicalConclusionNeeded = (piw) => {
    if (!getConfig('clinicalConclusionFormMandatory') || !piw) return false;
    return !_.isEmpty(piw?.diagnosis?.primary) && _.isNil(piw?.clinicalConclusion?.who);
};

// Converts from nutrition screen
export const cmToFeetInch = (cm) => {
    const realFeet = (cm * 0.3937) / 12;
    const feet = Math.floor(realFeet);
    const inches = Number((realFeet - feet) * 12).toFixed(2);
    return `${feet} ft ${inches} in`;
};

export const feetInchToCm = (ft, inch) => {
    return `${ft * 30.48 + inch * 2.54} cm`;
};

export function convertStatus(status) {
    // eslint-disable-next-line default-case
    switch (status) {
        case SEPSIS_TOOL_TYPE.COMPLETED:
            return 'Completed';
        case SEPSIS_TOOL_TYPE.CANCELED:
            return 'Canceled';
        case SEPSIS_TOOL_TYPE.COMPLETED_NOT_REQUIRED:
            return 'Completed but not required';
        case SEPSIS_TOOL_TYPE.IN_COMPLETED:
            return 'In Draft';
        case SEPSIS_TOOL_TYPE.NOT_REQUIRED:
            return 'Action not required';
    }
}

export const sepsisToolAgeParameter = (age, type) => {
    if (type === 'HR') {
        if (age < 1) {
            return '160';
        } else if (age >= 1 && age <= 2) {
            return '150';
        } else if (age > 2 && age <= 5) {
            return '140';
        } else if (age > 5 && age <= 11) {
            return '120';
        } else if (age > 11 && age <= 16) {
            return '100';
        } else {
            return '90';
        }
    }
    if (type === 'RR') {
        if (age < 1) {
            return '50';
        } else if (age >= 1 && age <= 2) {
            return '50';
        } else if (age > 2 && age <= 5) {
            return '40';
        } else if (age > 5 && age <= 11) {
            return '25';
        } else if (age > 11 && age <= 16) {
            return '20';
        } else {
            return '20';
        }
    }

    return undefined;
};

export const getSafeguardingValues = (patient) => {
    if (patient.studies?.mainScreenSafeguarding) return patient.studies?.mainScreenSafeguarding;

    let sTriage = patient.triage?.safeguarding ? patient.triage.safeguarding : [];
    let sClerking = patient.clerking?.safeguarding ? patient.clerking.safeguarding : [];
    let sSeeAndTreat = patient?.studies?.seeAndTreat?.safeguarding ? patient?.studies?.seeAndTreat?.safeguarding : [];
    let sPaedsSafeguarding = [];
    if (!_.isEmpty(patient?.studies?.paedSafeguarding?.list)) {
        _.forEach(patient?.studies?.paedSafeguarding?.list, (element) => {
            sPaedsSafeguarding.push(...element?.safeguarding?.reasons?.safeguarding);
        });
    }

    return _.uniqWith([...sClerking, ...sTriage, ...sSeeAndTreat, ...sPaedsSafeguarding], _.isEqual);
};

export const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\\\$&').replace(/[']/g, "\\'"); // $& means the whole matched string
};

export function getPatientTriageType(dob) {
    let age = moment().diff(moment(dob), 'years');

    let type = PATIENT_AGE_TYPE.ADULT;
    if (age >= 75) {
        type = PATIENT_AGE_TYPE.OVER75;
    }
    if (age < 18) {
        type = PATIENT_AGE_TYPE.PAEDS;
    }
    return type;
}

export const patientSafetyChecklistSectionValidation = (psc, section, pt) => {
    const version = getConfig('patientSafetyChecklist.clientVersion', 'DCH');
    const isUHD = version === 'UHD';
    const IVCannulaMandatory = getConfig('patientSafetyChecklist.IVCannulaMandatory', false);

    if (getConfig('patientSafetyChecklist.allFieldsMandatory', false)) {
        const checkValue = (val) => {
            return (!_.isNull(val?.value) && _.isBoolean(val?.value)) || (!_.isNull(val?.notRequired) && _.isBoolean(val?.notRequired));
        };

        const checkVIPScore = (value) => {
            let checkedValue = checkValue(value);
            const val = Number(value?.value);
            if (_.isNumber(val)) {
                checkedValue = checkedValue && (val > 1 ? !_.isEmpty(value?.comment) : true);
            }
            return checkedValue;
        };

        switch (section) {
            case 1:
                const age = moment().diff(pt?.dob, 'years');

                return (
                    checkValue(psc?.triage) &&
                    checkValue(psc?.obsFirst) &&
                    checkValue(psc?.obsEscalation) &&
                    checkValue(psc?.painScore) &&
                    checkValue(psc?.analgesia) &&
                    checkValue(psc?.IVAccess) &&
                    (IVCannulaMandatory && psc?.IVAccess?.value ? !_.isEmpty(psc?.IVAccess?.comment) : true) &&
                    checkValue(psc?.ECGRecorded) &&
                    checkValue(psc?.ECGReviewed) &&
                    checkValue(psc?.undressedAndGown) &&
                    checkValue(psc?.IDBand) &&
                    checkValue(psc?.painScore) &&
                    checkValue(psc?.analgesia) &&
                    checkValue(psc?.infectionScreening) &&
                    checkValue(psc?.patientAlertsFlags) &&
                    (isUHD ? true : checkValue(psc?.diabetic)) &&
                    checkValue(psc?.sepsisSuspected) &&
                    checkValue(psc?.sepsisSeniorSignOff) &&
                    checkValue(psc?.bloodTests) &&
                    (isUHD ? true : checkValue(psc?.strokeCall)) &&
                    (isUHD ? true : checkValue(psc?.specTeamsNotified)) &&
                    (age > 18 ? checkValue(psc?.dementiaScore) : true) &&
                    (isUHD ? checkValue(psc?.mentalHealth) : true) &&
                    (isUHD ? checkVIPScore(psc?.vipScore) : true) &&
                    (isUHD ? !_.isEmpty(psc?.nursingComments?.comment) : true)
                );

            case 2:
                return (
                    checkValue(psc?.obsSecond) &&
                    checkValue(psc?.painScoreAssessedSecond) &&
                    checkValue(psc?.analgesiaSecond) &&
                    checkValue(psc?.nok) &&
                    checkValue(psc?.nokContacted) &&
                    (isUHD ? true : checkValue(psc?.refreshmentSecond) && checkValue(psc?.pressureAreas)) &&
                    (isUHD ? true : checkValue(psc?.medicinesSecond)) &&
                    (isUHD ? true : checkValue(psc?.diabeticPatientsSecond)) &&
                    checkValue(psc?.criticalDrugsGiven) &&
                    (isUHD ? checkValue(psc?.mentalHealthSecond) : true) &&
                    (isUHD && psc?.mentalHealthSecond?.value ? !_.isEmpty(psc?.mentalHealthSecond?.comment) : true) &&
                    (isUHD ? !_.isEmpty(psc?.nursingCommentsSecond?.comment) : true) &&
                    (isUHD && psc?.IVAccessSecond?.value ? !_.isEmpty(psc?.IVAccessSecond?.comment) : true) &&
                    (isUHD ? checkVIPScore(psc?.vipScoreSecond) : true) &&
                    (isUHD ? checkValue(psc?.bloodTestsSecond) : true)
                );

            case 3:
                return (
                    checkValue(psc?.obsThird) &&
                    checkValue(psc?.painScoreAssessedThird) &&
                    checkValue(psc?.analgesiaThird) &&
                    checkValue(psc?.medicinesThird) &&
                    checkValue(psc?.refreshmentThird) &&
                    checkValue(psc?.patientUpdatePlan) &&
                    (isUHD ? true : checkValue(psc?.diabeticPatientsThird)) &&
                    (isUHD ? checkValue(psc?.mentalHealthThird) : true) &&
                    (isUHD && psc?.mentalHealthThird?.value ? !_.isEmpty(psc?.mentalHealthThird?.comment) : true) &&
                    (isUHD ? !_.isEmpty(psc?.nursingCommentsThird?.comment) : true) &&
                    (isUHD && psc?.IVAccessThird?.value ? !_.isEmpty(psc?.IVAccessThird?.comment) : true) &&
                    (isUHD ? checkVIPScore(psc?.vipScoreThird) : true) &&
                    (isUHD ? checkValue(psc?.bloodTestsThird) : true)
                );

            case 4:
                return (
                    checkValue(psc?.obsFourth) &&
                    checkValue(psc?.painScoreAssessedFourth) &&
                    checkValue(psc?.analgesiaFourth) &&
                    checkValue(psc?.refreshmentFourth) &&
                    checkValue(psc?.medicinesFourth) &&
                    (isUHD ? true : checkValue(psc?.diabeticPatientsFourth)) &&
                    (isUHD ? checkValue(psc?.patientUpdatePlanFourth) : true) &&
                    (isUHD ? checkValue(psc?.mentalHealthFourth) : true) &&
                    (isUHD && psc?.mentalHealthFourth?.value ? !_.isEmpty(psc?.mentalHealthFourth?.comment) : true) &&
                    (isUHD ? !_.isEmpty(psc?.nursingCommentsFourth?.comment) : true) &&
                    (isUHD && psc?.IVAccessFourth?.value ? !_.isEmpty(psc?.IVAccessFourth?.comment) : true) &&
                    (isUHD ? checkVIPScore(psc?.vipScoreFourth) : true) &&
                    (isUHD ? checkValue(psc?.bloodTestsFourth) : true)
                );

            case 0:
            case 6:
                if (section === 6 && isUHD) {
                    return (
                        !_.isEmpty(psc?.reasonForDelay?.comment) &&
                        checkValue(psc.delayExplained) &&
                        checkValue(psc.transferredIntoHospitalBed) &&
                        checkValue(psc.nurseAware) &&
                        checkValue(psc?.medicines) &&
                        checkValue(psc?.mentalHealth) &&
                        (psc?.mentalHealth?.value ? !_.isEmpty(psc?.mentalHealth?.comment) : true) &&
                        !_.isEmpty(psc?.nursingComments?.comment) &&
                        (isUHD && psc?.IVAccess?.value ? !_.isEmpty(psc?.IVAccess?.comment) : true) &&
                        (isUHD ? checkVIPScore(psc?.vipScore) : true) &&
                        (isUHD ? checkValue(psc?.bloodTests) : true)
                    );
                }

                return (
                    checkValue(psc?.obs) &&
                    checkValue(psc.painScore) &&
                    checkValue(psc.analgesia) &&
                    checkValue(psc.refreshment) &&
                    checkValue(psc.medicines) &&
                    (isUHD ? true : checkValue(psc?.diabeticPatients)) &&
                    (isUHD ? checkValue(psc?.patientUpdatePlan) : true) &&
                    (isUHD ? checkValue(psc?.mentalHealth) : true) &&
                    (isUHD && psc?.mentalHealth?.value ? !_.isEmpty(psc?.mentalHealth?.comment) : true) &&
                    (isUHD ? !_.isEmpty(psc?.nursingComments?.comment) : true) &&
                    (isUHD && psc?.IVAccess?.value ? !_.isEmpty(psc?.IVAccess?.comment) : true) &&
                    (isUHD ? checkVIPScore(psc?.vipScore) : true) &&
                    (isUHD ? checkValue(psc?.bloodTests) : true)
                );

            default:
                console.error('Invalid Section', section);
        }
    } else {
        switch (section) {
            case 1:
                return (
                    (psc?.triage?.value || psc?.triage?.notRequired) &&
                    (psc?.obsFirst?.value || psc?.obsFirst?.notRequired) &&
                    (psc?.obsEscalation?.value || psc?.obsEscalation?.notRequired) &&
                    (psc?.painScore?.value || psc?.painScore?.notRequired) &&
                    (psc?.analgesia?.value || psc?.analgesia?.notRequired) &&
                    (IVCannulaMandatory ? psc?.IVAccess?.value || psc?.IVAccess?.notRequired : true)
                );

            case 2:
                return (
                    (psc?.obsSecond?.value || psc?.obsSecond?.notRequired) &&
                    (psc?.painScoreAssessedSecond?.value || psc?.painScoreAssessedSecond?.notRequired) &&
                    (psc?.analgesiaSecond?.value || psc?.analgesiaSecond?.notRequired)
                );

            case 3:
                return (
                    (psc?.obsThird?.value || psc?.obsThird?.notRequired) &&
                    (psc?.painScoreAssessedThird?.value || psc?.painScoreAssessedThird?.notRequired) &&
                    (psc?.analgesiaThird?.value || psc?.analgesiaThird?.notRequired)
                );

            case 4:
                return (
                    (psc?.obsFourth?.value || psc?.obsFourth?.notRequired) &&
                    (psc?.painScoreAssessedFourth?.value || psc?.painScoreAssessedFourth?.notRequired) &&
                    (psc?.analgesiaFourth?.value || psc?.analgesiaFourth?.notRequired)
                );

            case 0:
            case 6:
                if (section === 6 && isUHD) return true;
                return (
                    (psc?.obs?.value || psc?.obs?.notRequired) &&
                    (psc.painScore?.value || psc.painScore?.notRequired) &&
                    (psc.analgesia?.value || psc.analgesia?.notRequired)
                );

            default:
                console.error('Invalid Section', section);
        }
    }
};

export const patientSafetyCheckListLastSectionValid = (psc, pt) => {
    const validSection1 = patientSafetyChecklistSectionValidation(psc, 1, pt);
    const validSection2 = validSection1 && patientSafetyChecklistSectionValidation(psc, 2);
    const validSection3 = validSection2 && patientSafetyChecklistSectionValidation(psc, 3);
    return validSection3 && patientSafetyChecklistSectionValidation(psc, 4);
};

export const getLastAtoEAssessment = (currentPatientInWard, checkFilled = false) => {
    if (checkFilled) {
        return _.isArray(currentPatientInWard?.studies?.nonContactObs) && !_.isEmpty(currentPatientInWard?.studies?.nonContactObs);
    }

    if (currentPatientInWard?.studies?.nonContactObs && !_.isEmpty(currentPatientInWard?.studies?.nonContactObs)) {
        const lastObs = _.last(currentPatientInWard?.studies?.nonContactObs);
        if (lastObs) {
            return lastObs;
        }
    }
};

export const MainButtonBackground = {
    RED: '#A32626',
    ORANGE: '#F26B28',
    GREEN: '#01AF69',
    BLUE: '#2180f7',
};

export const getCSMFormName = () => {
    const isUHDClient = getConfig('client', null) === 'UHD';
    if (isUHDClient) return 'Bed Request';
    return getConfig('modules.bedManagement.bedRequest') ? 'Bed Request' : 'CSM Contact Time';
};

export const formSmokeIsValid = (form) => {
    return form && form.smoker !== null && (form.smoker !== 'Smoker' || form.cigarettesPerDay !== '');
};

const isDateValid = (date) => {
    return date && moment(date).isSameOrAfter(moment());
};

const getIconName = (name, status) => {
    if (name !== 'covid') {
        const suffix = status === 'suspected' ? 'S' : '';
        return name + suffix;
    } else {
        // eslint-disable-next-line default-case
        switch (status) {
            case 'suspected':
                return 'covid';
            case 'confirm':
                return 'covidPositive';
            case 'none':
                return 'covidNegative';
        }
    }
};

const getInfectionColor = (status) => {
    // eslint-disable-next-line default-case
    switch (status) {
        case 'suspected':
            return '#FEB03A';
        case 'confirm':
            return '#DC4A5D';
        case 'none':
            return '#01AF69';
    }
};

export const getInfectionIcon = (metadata, className, onlyCovid = false, tooltip, renderEmpty) => {
    const infectionSet = new Map();
    const iconsToRedner = [];

    // Prepare data
    if (metadata) {
        if (metadata?.covid && isDateValid(metadata.covid?.expired)) infectionSet.set('covid', metadata.covid); // for old data
        if (metadata.infections && !onlyCovid) {
            for (const key in metadata.infections) {
                if (isDateValid(metadata.infections[key]?.expired)) infectionSet.set(key, metadata.infections[key]);
            }
        }
    }

    if (!_.isEmpty(infectionSet)) {
        infectionSet.forEach((val, key) => {
            const icon = getIconName(key, val?.status);
            const color = getInfectionColor(val?.status);
            iconsToRedner.push(<AgyleNamedIcon key={key} name={icon} style={{ color }} withTooltip={tooltip || false} />);
        });
    }

    if (!_.isEmpty(iconsToRedner)) {
        return <div className={className || ''}>{iconsToRedner.map((inf) => inf)}</div>;
    } else {
        if (renderEmpty) return '-';
    }
};

export const formAuditIsValid = (form) => {
    if (!form) return false;

    // Support nested shape (form.base/form.extra) or flat shape with top-level keys
    const base = form.base || {
        howOftenDrinkAlcohol: form.howOftenDrinkAlcohol,
        standardDrinkDay: form.standardDrinkDay,
        howOftenMoreDrink: form.howOftenMoreDrink,
    };

    const extra = form.extra || {
        notAbleToStopDrinking: form.notAbleToStopDrinking,
        failedBecauseDrinking: form.failedBecauseDrinking,
        neededAlcoholicMorning: form.neededAlcoholicMorning,
        unableRememberWhatHappened: form.unableRememberWhatHappened,
        injuredResultDrinking: form.injuredResultDrinking,
        relativeConcerned: form.relativeConcerned,
    };

    const isEmpty = (v) => v === '' || v === null || v === undefined;

    // Base fields required
    let drinkBaseFailed = Object.keys(base).some((key) => {
        const val = base[key];
        if (isEmpty(val)) return true;
        if (typeof val === 'object' && val.hasOwnProperty('value')) return isEmpty(val.value);
        return false;
    });

    // Compute base score (support primitive values or option objects)
    let score = 0;
    Object.keys(base).forEach((key) => {
        const v = base[key];
        if (isEmpty(v)) return;
        const num = typeof v === 'object' && v.hasOwnProperty('value') ? Number(v.value) : Number(v);
        if (!Number.isNaN(num)) score += num;
    });

    // If base score high, extra fields become required. Also consider any additional keys present in extra as required.
    let drinkExtraFailed = false;
    if (score > 4) {
        drinkExtraFailed = Object.keys(extra).some((key) => {
            const val = extra[key];
            if (isEmpty(val)) return true;
            if (typeof val === 'object' && val.hasOwnProperty('value')) return isEmpty(val.value);
            return false;
        });
    }

    return !(drinkBaseFailed || drinkExtraFailed);
};

export const capitalizeObjectValues = (data) => {
    return Object.keys(data).reduce((acc, key) => {
        const value = data[key];

        acc[key] = typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1) : value;

        return acc;
    }, {});
};

const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

export const mapReferralDataToFormStructure = (data) =>
    capitalizeObjectValues({
        allergyIntoleranceFor: data?.allergyintolerance?.code?.coding?.[0].display || '-',
        clinicalStatus: data?.allergyintolerance?.clinicalStatus?.coding?.[0]?.display || '-',
        lastUpdated: data?.allergyintolerance?.meta?.lastUpdated
            ? moment(data.allergyintolerance.meta.lastUpdated).format(defaultDateTimeFormat)
            : '-',
        verificationStatus: data?.allergyintolerance?.verificationStatus?.coding?.[0]?.display || '-',
        allergyIntoleranceCategory: data?.allergyintolerance?.category?.[0] || '-',
        reactionSeverity: data?.allergyintolerance?.reaction?.[0]?.severity || '-',
        reactionManifestation: data?.allergyintolerance?.reaction?.[0]?.manifestation?.[0]?.coding?.[0]?.display || '-',

        ecdsAcuityScore: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[7]?.coding?.[0]?.code || null,
        ecdsAcuityScoreTitle: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[7]?.coding?.[0]?.display || '-',

        carePlanIntent: data?.careplan?.intent || '-',
        carePlanStatus: data?.careplan?.status || '-',
        carePlanLastUpdated: data?.careplan?.meta?.lastUpdated ? moment(data.careplan.meta.lastUpdated).format(defaultDateTimeFormat) : '-',
        carePeriodStart: data?.careplan?.period?.start ? moment(data.careplan.period.start).format(defaultDateTimeFormat) : '-',

        pathwaysAssessmentSD: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[0]?.coding?.[0]?.display || '-',
        pathwaysAssessmentSDCode: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[0]?.coding?.[0]?.code || '-',

        pathwaysAssessmentDX: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[2]?.coding?.[0]?.display || '-',
        pathwaysAssessmentDXCode: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[2]?.coding?.[0]?.code || '-',

        presentingComplaintselectedPathwayTemplate: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[3]?.coding?.[0]?.display || '-',
        presentingComplaintselectedPathwayTemplateCode:
            data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[3]?.coding?.[0]?.code || '-',

        pathwaysAssessmentSGChiefComplaint: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[1]?.coding?.[0]?.display || '-',
        pathwaysAssessmentSGCodeChiefComplaint: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[1]?.coding?.[0]?.code || '-',

        pathwayAssessment: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[5]?.text
            ? (data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[5]?.text).replaceAll('/n', '\n')
            : '-',
        safeguarding: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[4]?.text
            ? (data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[4]?.text).replaceAll('/n', '\n')
            : '-',
        personExpectationsAndWishes: data?.careplan?.supportingInfo?.[0]?.display || '-',
        safeguardingConcernsFlag: data?.careplan?.supportingInfo?.[1]?.display || '-',
        carePlanAdditionalInformationSources: data?.careplan?.supportingInfo?.[2]?.display || '-',
        attendedWith: data?.patient?.contact?.[0]?.relationship?.[0]?.coding?.[0]?.display || '-',

        consentDate: data?.consent?.dateTime ? moment(data.consent.dateTime).format(defaultDateTimeFormat) : '-',
        consentPolicyRuleCode: data?.consent?.policyRule?.coding?.[0]?.code || '-',
        consentPolicyRule: data?.consent?.policyRule?.coding?.[0]?.display || '-',
        consentRejectedStatus: data?.consent?.status || '-',
        consentCode: data?.consent?.scope?.coding?.[0]?.code || '-',
        consentScope: data?.consent?.scope?.coding?.[0]?.display || '-',
        consentCategoryCode: data?.consent?.category?.[0]?.coding?.[0]?.code || '-',
        consentCategory: data?.consent?.category?.[0]?.coding?.[0]?.display || '-',
        rejectedServiceDate: data?.questionnaireresponse?.authored
            ? moment(data.questionnaireresponse.authored).format(defaultDateTimeFormat)
            : '-',
        rejectedServiceId: data?.questionnaireresponse?.item?.[0]?.item?.[0]?.item?.[0]?.answer?.[0]?.valueString || '-',
        rejectedServiceName: data?.questionnaireresponse?.item?.[0]?.item?.[0]?.item?.[1]?.answer?.[0]?.valueString || '-',
        rejectedServiceReason: data?.questionnaireresponse?.item?.[0]?.item?.[0]?.item?.[2]?.answer?.[0]?.valueCoding?.display || '-',
        rejectedServiceComment: data?.questionnaireresponse?.item?.[0]?.item?.[0]?.item?.[3]?.answer?.[0]?.valueString || '-',

        extensionValue: data?.patient?.contact?.[0]?.extension?.[0]?.valuePositiveInt || '-',
        contactTelecomRank: data?.patient?.contact?.[0]?.telecom?.[0]?.rank || '-',
        nameFamily: data?.patient?.contact?.[0]?.name?.family || '-',
        nameGiven: data?.patient?.contact?.[0]?.name?.given?.[0] || '-',
        contactRelationshipDisplay: data?.patient?.contact?.[0]?.relationship?.[0]?.coding?.[0]?.display || '-',
        contactTelecomSystem: data?.patient?.contact?.[0]?.telecom?.[0]?.system || '-',
        contactTelecomUse: data?.patient?.contact?.[0]?.telecom?.[0]?.use || '-',
        contactTelecomValue: data?.patient?.contact?.[0]?.telecom?.[0]?.value || '-',

        nhsPatientNumber: data?.patient?.identifier?.[0]?.value || '-',
        nhsPatientStatus: data?.patient?.identifier?.[0]?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display || '-',
        gender: data?.patient?.gender || '-',
        sexAtBirth: data?.observation?.code?.coding?.[0]?.display || '-',
        refNumber: data?.encounter_recv?.identifier?.[0]?.value || '-',

        //pathwayAssessmentAdditional: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[5]?.text || "-",
        consultationSummary: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[6]?.text || '-',

        consentDateAndTime: data?.consent?.dateTime || '-',
        consentStatus: data?.consent?.status || '-',

        medicationStatementDateAsserted: data?.medicationstatement?.dateAsserted || '-',
        effectiveDateTime: data?.medicationstatement?.effectiveDateTime || '-',
        medicationStatementCategory: data?.medicationstatement?.category?.coding?.[0]?.display || '-',
        medication: data?.medicationstatement?.medicationCodeableConcept?.coding?.[0]?.display || '-',
        dosage: data?.medicationstatement?.dosage?.[0]?.text || '-',
        dosageRoute: data?.medicationstatement?.dosage?.[0]?.route?.coding?.[0]?.display || '-',
        dosageFrequency: data?.medicationstatement?.dosage?.[0]?.timing?.repeat?.frequency || '-',
        duration:
            data?.medicationstatement?.dosage?.[0]?.timing?.repeat?.period &&
            data?.medicationstatement?.dosage?.[0]?.timing?.repeat?.periodUnit
                ? `${data.medicationstatement.dosage?.[0].timing.repeat.period} ${data.medicationstatement.dosage?.[0].timing.repeat.periodUnit}`
                : '-',
        reasonCode: data?.medicationstatement?.reasonCode?.[0]?.coding?.[0]?.code || '-',
        symptom: data?.medicationstatement?.reasonCode?.[0]?.coding?.[0]?.display || '-',

        observationDateAsserted: '???',
        observationStatus: data?.observation?.status || '-',
        expectationsAndWishes: data?.observation?.code?.text || '-',
        observationNoteAuthor: data?.observation?.note?.[0]?.authorString || '-',
        observationNote: data?.observation?.note?.[0]?.text || '-',

        providedBy: data?.organization?.name || '-',

        idNumber: data?.practitioner?.identifier?.[0]?.value || '-',
        name: data?.practitioner?.name?.[0]?.family || '-',
        surname: data?.practitioner?.name?.[0]?.given?.[0] || '-',
        contactMethod: data?.practitioner?.telecom?.[0]?.system || '-',
        contactType: data?.practitioner?.telecom?.[0]?.use || '-',
        telephoneNumber: data?.practitioner?.telecom?.[0]?.value || '-',

        role: data?.practitionerrole?.code?.[0]?.coding?.[0]?.display || '-',
        speciality: data?.practitionerrole?.specialty?.[0]?.coding?.[0]?.display || '-',

        questionnaireResponseAdditionalInformationSources: '???',
        additionalSources: '???',
        additionalLookupSources: '???',
        sourceName: data?.questionnaireresponse?.item?.[0]?.item?.[0]?.item?.[0]?.answer?.[0]?.valueString || '-',
        source: data?.questionnaireresponse?.item?.[0]?.item?.[1]?.item?.[1]?.answer?.[0]?.valueUri || '-',
        sourceName2: data?.questionnaireresponse?.item?.[0]?.item?.[2]?.item?.[0]?.answer?.[0]?.valueString || '-',
        spnTitle: data?.questionnaireresponse?.item?.[0]?.item?.[2]?.item?.[1]?.answer?.[0]?.valueString || '-',
        spnDetails: data?.questionnaireresponse?.item?.[0]?.item?.[2]?.item?.[2]?.answer?.[0]?.valueString || '-',
        spnActiveDate: data?.questionnaireresponse?.item?.[0]?.item?.[2]?.item?.[3]?.answer?.[0]?.valueDateTime
            ? moment(data.questionnaireresponse.item?.[0].item?.[2].item?.[3].answer?.[0].valueDateTime).format(defaultDateTimeFormat)
            : '-',
        spnReviewDate: data?.questionnaireresponse?.item?.[0]?.item?.[2]?.item?.[4]?.answer?.[0]?.valueDateTime
            ? moment(data.questionnaireresponse.item?.[0].item?.[2].item?.[4].answer?.[0].valueDateTime).format(defaultDateTimeFormat)
            : '-',

        serviceRequestIntent: data?.servicerequest?.intent || '-',
        serviceRequestStatus: data?.servicerequest?.status || '-',
        rejectedServicesDetails: data?.servicerequest?.supportingInfo?.[1]?.display || '-',
        rejectedServicesFlags: data?.servicerequest?.supportingInfo?.[0]?.display || '-',
        serviceRequestCategory: data?.servicerequest?.category?.[0]?.coding?.[0]?.display || '-',

        authoredOn: data?.servicerequest?.authoredOn ? moment(data.servicerequest.authoredOn).format(defaultDateTimeFormat) : '-',
        occurrencePeriod: data?.servicerequest?.occurrencePeriod?.start
            ? moment(data.servicerequest.occurrencePeriod.start).format(defaultDateTimeFormat)
            : '-',
        patientInstruction: data?.servicerequest?.patientInstruction || '-',
    });

export const mapReferralDataToForm = (data) => ({
    ecdsAcuityScore: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[7]?.coding?.[0]?.code || null,
    ecdsAcuityScoreTitle: data?.careplan?.activity?.[0]?.outcomeCodeableConcept?.[7]?.coding?.[0]?.display || null,
});

export const getFacility = (code) => {
    switch (code) {
        case 'R0D01':
            return 'Poole';
        case 'R0D02':
            return 'Bournemouth';
        case 'R0D02_NEW':
            return 'Bournemouth (New)';
        default:
            return null;
    }
};

export const getUsersTimeStamps = (user, wardPatientsObject) => {
    let styleTimeStamp = {
        display: 'inline-block',
        lineHeight: '12px',
        border: '1px solid red',
        borderRadius: '7px',
        minWidth: 14,
        padding: '0 2px',
        height: 14,
        textAlign: 'center',
        fontSize: '10px',
        marginLeft: 6,
    };
    let timeStampCount = 0;

    _.forEach(wardPatientsObject, (patient) => {
        if (
            patient.clerking?.clinician &&
            patient.clerking.status === 'timestamp' &&
            patient.clerking.clinician &&
            patient.clerking.clinician.userName === user.userName
        ) {
            timeStampCount++;
        }
        if (patient.seen && patient.seen.status === 'timestamp' && patient.seen.who && patient.seen.who.userName === user.userName) {
            timeStampCount++;
        }
        if (
            patient.studies?.seeAndTreat &&
            patient.studies.seeAndTreat.status === 'timestamp' &&
            patient.studies.seeAndTreat.clinician &&
            patient.studies.seeAndTreat.clinician.userName === user.userName
        ) {
            timeStampCount++;
        }
        if (
            patient.studies?.discharge &&
            patient.studies.discharge.status === 'timestamp' &&
            patient.studies.discharge.who &&
            patient.studies.discharge.who.userName === user.userName
        ) {
            timeStampCount++;
        }
        if (
            patient &&
            patient.triage &&
            patient.triage.triageClinicians &&
            !patient.triage.discriminator &&
            patient.triage.triageClinicians &&
            patient.triage.triageClinicians.userName === user.userName
        ) {
            timeStampCount++;
        }
    });
    return timeStampCount ? <div style={styleTimeStamp}> {timeStampCount} </div> : '';
};
